import { FunctionComponent, useEffect } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { getGlobalMapRef } from "features/map/GlobalViewState"
import { pushCursorStack, popCursorStack } from "features/map/MapElements"
import { clearSelection } from "panels/content/side-panel/slice.new"
import { setActiveTool } from "../tool-registry"
import { setCommentPlacingState } from "./CommentSlice"
import { selectCommentPlacementState } from "features/entity-caching/map-comments/selectors"

const commentCursor = `url("/images/cursors/cursor-comment.svg") 0 24, auto`

const dragThresholdPixels = 4

export const MapCommentPortal: FunctionComponent = () => {
    const dispatch = useAppDispatch()
    const commentState = useAppSelector(selectCommentPlacementState)
    const isPlacing = commentState.mode === "placing"

    useEffect(() => {
        if (!isPlacing) return

        const map = document.getElementById("map")
        if (!map) return

        pushCursorStack(commentCursor, { sticky: true })

        let down: { x: number; y: number } | undefined

        const placeComment = (event: MouseEvent) => {
            if (event.button !== 0 || !down) return
            // Placing a comment wins over whatever layer sits under the cursor.
            event.stopPropagation()
            event.preventDefault()
            const movedX = Math.abs(event.clientX - down.x)
            const movedY = Math.abs(event.clientY - down.y)
            down = undefined
            if (movedX > dragThresholdPixels || movedY > dragThresholdPixels) return

            const maplibre = getGlobalMapRef()?.current?.getMap()
            const canvas = maplibre?.getCanvas()
            if (!maplibre || !canvas) return

            const rect = canvas.getBoundingClientRect()
            const coordinates = maplibre.unproject([
                event.clientX - rect.left,
                event.clientY - rect.top,
            ])
            if (!coordinates) return

            const { lng, lat } = coordinates
            dispatch(setCommentPlacingState({ mode: "writing", lng, lat }))
        }

        const onMouseDown = (event: MouseEvent) => {
            // Pressing on an existing marker selects it rather than stacking a
            // new comment on top.
            if (event.button !== 0 || (event.target as Element).closest("[data-map-node]")) return
            down = { x: event.clientX, y: event.clientY }
        }

        // A release that never lands on #map produces no click, so the press
        // must be forgotten here or it swallows the next one.
        const onMouseUp = (event: MouseEvent) => {
            if (!(event.target as Element | null)?.closest("#map")) down = undefined
        }

        const cancelComment = () => {
            dispatch(clearSelection())
            dispatch(setActiveTool(undefined))
        }
        const onContextMenu = (event: MouseEvent) => {
            event.preventDefault()
            cancelComment()
        }
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") cancelComment()
        }

        map.addEventListener("mousedown", onMouseDown, true)
        map.addEventListener("click", placeComment, true)
        window.addEventListener("mouseup", onMouseUp)
        window.addEventListener("contextmenu", onContextMenu)
        window.addEventListener("keydown", onKeyDown)

        return () => {
            popCursorStack()
            map.removeEventListener("mousedown", onMouseDown, true)
            map.removeEventListener("click", placeComment, true)
            window.removeEventListener("mouseup", onMouseUp)
            window.removeEventListener("contextmenu", onContextMenu)
            window.removeEventListener("keydown", onKeyDown)
        }
    }, [isPlacing, dispatch])

    return null
}
